import { useTelegramData } from './useTelegram.js'

/**
 * Composable для работы с тактильной обратной связью (haptics) в Telegram Mini App
 * 
 * Telegram Web App API поддерживает следующие методы:
 * - impactOccurred(style) - для тактильной обратной связи при нажатиях
 * - notificationOccurred(type) - для уведомлений
 * - selectionChanged() - при изменении выбора
 */
export function useHaptics() {
  const { tg } = useTelegramData()

  /**
   * Получает объект Telegram WebApp (с fallback)
   */
  const getTelegramWebApp = () => {
    if (tg) return tg
    // Fallback на прямую работу с Telegram WebApp
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      return window.Telegram.WebApp
    }
    return null
  }

  /**
   * Проверяет доступность HapticFeedback API
   */
  const isAvailable = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp) return false
    return typeof telegramWebApp.HapticFeedback !== 'undefined' && 
           typeof telegramWebApp.HapticFeedback.impactOccurred === 'function'
  }

  /**
   * Легкая вибрация - для обычных нажатий (кнопки, ссылки)
   */
  const light = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.impactOccurred('light')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Средняя вибрация - для важных действий (подтверждения, отправка форм)
   */
  const medium = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.impactOccurred('medium')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Сильная вибрация - для критических действий (удаление, важные подтверждения)
   */
  const heavy = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.impactOccurred('heavy')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Жесткая вибрация - для жестких действий
   */
  const rigid = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.impactOccurred('rigid')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Мягкая вибрация - для мягких действий
   */
  const soft = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.impactOccurred('soft')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Уведомление об успехе
   */
  const success = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.notificationOccurred('success')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Уведомление об ошибке
   */
  const error = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.notificationOccurred('error')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Уведомление-предупреждение
   */
  const warning = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.notificationOccurred('warning')
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  /**
   * Вибрация при изменении выбора (для селектов, переключателей)
   */
  const selectionChanged = () => {
    const telegramWebApp = getTelegramWebApp()
    if (!telegramWebApp || !isAvailable()) return
    try {
      telegramWebApp.HapticFeedback.selectionChanged()
    } catch (error) {
      console.warn('Haptic feedback error:', error)
    }
  }

  return {
    isAvailable,
    light,
    medium,
    heavy,
    rigid,
    soft,
    success,
    error,
    warning,
    selectionChanged
  }
}
